import { IUserDocument, IHierarchyItem, IUIUser, ISupervisor, ITimecardEmployee, ELoggedInUserType } from "../models"
import AccountStore from "../stores/AccountStore"
import AuthenticationStore from "../stores/AuthenticationStore"
import * as permissionHelper from "../helpers/permissionHelper"
import * as hierarchyHelper from "./hierarchyHelper"
import strings from "../localization/strings"
import _ from "lodash"
import * as api from "../api"

export function getEmployeeAccessHierarchyIds(employee: IUserDocument | IUIUser): string[] {
    return AccountStore.getExplicitEmployeeHierarchyAssignment() ?
        employee.LinkedHierarchyIds :
        hierarchyHelper.getHierarchyIdsForShowAllChildHierarchyData(true, employee.DefaultHierarchyItemId)
}

export function getEmployeeClickableHierarchyIds(employee: IUserDocument | IUIUser): string[] {
    const employeeAccessHierarchyIds = getEmployeeAccessHierarchyIds(employee)
    return AccountStore.getExplicitEmployeeHierarchyAssignment() ?
        getAllHierarchyIdsToRoot(employeeAccessHierarchyIds) :
        employeeAccessHierarchyIds
}

export function getSupervisorAccessHierarchyIds(supervisor: ISupervisor = null): string[] {
    if (supervisor === null)
        supervisor = AuthenticationStore.getLoggedInUser() as ISupervisor

    let returnList: string[] = []
    const allAccessHierarchyIds = hierarchyHelper.getHierarchyIdsForShowAllChildHierarchyData(true, null)
    const isAccountAdmin = permissionHelper.isAccountAdmin(supervisor.UserRoleId)

    if (isAccountAdmin) {
        returnList = allAccessHierarchyIds
    }
    else {
        if (AccountStore.getExplicitAccountUserHierarchyAssignment()) {
            const assignedSupervisorIdsAtAccount = AccountStore.getCurrentAccount().LinkedUserIds ? AccountStore.getCurrentAccount().LinkedUserIds : []
            if (assignedSupervisorIdsAtAccount.includes(supervisor.Id))
                returnList.push(null)
            AccountStore.getCurrentAccount().HierarchyItems.forEach(item =>
                item.LinkedUserIds && item.LinkedUserIds.includes(supervisor.Id) &&
                returnList.push(item.Id)
            )
        }
        else {
            const viewFullHierarchyPermission = permissionHelper.hasPermission(AuthenticationStore.Permissions.Account.ViewFullHierarchy, supervisor)
            returnList = !supervisor.OrganizationHierarchyId || viewFullHierarchyPermission ?
                allAccessHierarchyIds :
                hierarchyHelper.getHierarchyIdsForShowAllChildHierarchyData(true, supervisor.OrganizationHierarchyId)
        }
    }
    return returnList
}

export function getSupervisorAccessPermissionForHierarchyId(hierarchyId: string, supervisor: ISupervisor = null): boolean {
    const supervisorAccessHierarchyIds = getSupervisorAccessHierarchyIds(supervisor)
    return supervisorAccessHierarchyIds && supervisorAccessHierarchyIds.includes(hierarchyId ? hierarchyId : null)
}

export function getSupervisorAccessPermissionForHierarchy(hierarchyItem: IHierarchyItem, supervisor: ISupervisor = null): boolean {
    return getSupervisorAccessPermissionForHierarchyId(hierarchyItem && hierarchyItem.Id ? hierarchyItem.Id : null, supervisor)
}

// if explicit assign, supervisor can click uplevel than assigned hierarchy to move another assigned hierarchy
// else, supervisor can click assigned hierarchies 
export function getSupervisorClickableHierarchyIds(supervisor: ISupervisor = null): string[] {
    const supervisorAccessHierarchyIds = getSupervisorAccessHierarchyIds(supervisor)
    return AccountStore.getExplicitAccountUserHierarchyAssignment() ?
        getAllHierarchyIdsToRoot(supervisorAccessHierarchyIds) :
        supervisorAccessHierarchyIds
}

export function getSupervisorClickableAccessForHierarchy(hierarchyId: string, supervisor: ISupervisor = null): boolean {
    const clickableAccessHierarchyIds = getSupervisorClickableHierarchyIds(supervisor)
    return clickableAccessHierarchyIds.includes(hierarchyId ? hierarchyId : null)
}

// let's use this function when supervisor choose hierarchy for employee punch
// if explicit assign account user, supervisor can click uplevel than assigned hierarchy to move another assigned hierarchy
// else, supervisor can click assigned hierarchies 
export function getSupervisorClickableHierarchyIdsWithEmployeeAccess(employee: IUIUser, supervisor: ISupervisor = null): string[] {
    const supervisorAccessHierarchyIds = getSupervisorAccessHierarchyIds(supervisor)
    const employeeAccessHierarchyIds = getEmployeeAccessHierarchyIds(employee)
    const intersectionHierarchyIds = supervisorAccessHierarchyIds.filter(item => employeeAccessHierarchyIds.includes(item))
    return AccountStore.getExplicitAccountUserHierarchyAssignment() ?
        getAllHierarchyIdsToRoot(intersectionHierarchyIds) :
        intersectionHierarchyIds
}

// get the hierarchies which all employees of GlobalPunch access to
export function getGlobalPunchEmployeesAccessHierarchyIds(employees: IUIUser[]): string[] {
    const employeesAccessHierarchyIds = employees.map(employee => getEmployeeAccessHierarchyIds(employee))
    return _.intersection(...employeesAccessHierarchyIds)
}

// let's use this function when supervisor choose hierarchy for Global punch
// if explicit assign account user, supervisor can click uplevel than assigned hierarchy to move another assigned hierarchy
// else, supervisor can click assigned hierarchies of employees
export function getSupervisorClickableHierarchyIdsWithGlobalPunchEmployeesAccess(employees: IUIUser[], supervisor: ISupervisor = null): string[] {
    const supervisorAccessHierarchyIds = getSupervisorAccessHierarchyIds(supervisor)
    const employeesAccessHierarchyIds = getGlobalPunchEmployeesAccessHierarchyIds(employees)
    const intersectionHierarchyIds = supervisorAccessHierarchyIds.filter(item => employeesAccessHierarchyIds.includes(item))
    return AccountStore.getExplicitAccountUserHierarchyAssignment() ?
        getAllHierarchyIdsToRoot(intersectionHierarchyIds) :
        intersectionHierarchyIds
}

// if explicit assign, need to get all hierarchy ids than assigned hierarchyIds
function getAllHierarchyIdsToRoot(accessHierarchyIds: string[]): string[] {
    const returnList: string[] = []
    accessHierarchyIds.forEach(id =>
        hierarchyHelper.getHierarchyPathToRoot(id).forEach(item => {
            const hierarchyItemId = item.hierarchyItem.Id ? item.hierarchyItem.Id : null
            !returnList.includes(hierarchyItemId) &&
                returnList.push(hierarchyItemId)
        })
    )
    return returnList
}

// non explicit employee assign
// this function is to check employee DefaultHierarchyItemId and DefaultPunchHierarchyId
export function getEmployeeValidationForDefaultAndPunch(defaultHierarchyItemId: string, defaultPunchHierarchyId: string): boolean {
    return defaultHierarchyItemId ?
        defaultPunchHierarchyId ?
            hierarchyHelper.getHierarchyItemById(defaultPunchHierarchyId).HierarchyList.includes(defaultHierarchyItemId) :
            false :
        true
}

export function getEmployeeAssignValidationForDefaultHierarchy(defaultHierarchyItemId: string): boolean {
    return AccountStore.getExplicitEmployeeHierarchyAssignment() ?
        defaultHierarchyItemId ?
            hierarchyHelper.getHierarchyDefinitionByHierarchyItemId(defaultHierarchyItemId).EnableEmployees :
            false :
        true
}

export function getSupervisorAssignValidationForOrganizationHierarchy(organizationHierarchyId: string, isAdmin: boolean): boolean {
    return AccountStore.getExplicitAccountUserHierarchyAssignment() ?
        organizationHierarchyId ?
            hierarchyHelper.getHierarchyDefinitionByHierarchyItemId(organizationHierarchyId).EnableAccountUsers :
            isAdmin :
        true
}

// it used for AddAccountUser, UserForm and UserDetailierarchyTable
export function getSupervisorChooseEmployeeDefaultHierarchyItemErrorStr(defaultHierarchyItem: IHierarchyItem): string {
    const accessRight = getSupervisorAccessPermissionForHierarchy(defaultHierarchyItem)
    const assignRight = getEmployeeAssignValidationForDefaultHierarchy(defaultHierarchyItem ? defaultHierarchyItem.Id : null)
    return accessRight ?
        assignRight ?
            null :
            strings.This_hierarchy_item_cant_be_assigned :
        strings.You_cant_access_this_hierarchy_item
}

export function getValidSubPages() {
    const subPages = ["timecards", "positions", "hierarchies", "records", "devices", "facespaces"]
    if (!AccountStore.getCurrentAccountSettings().FeatureSettings.EnableTimecards || !permissionHelper.hasPermission(AuthenticationStore.Permissions.Timecards.ViewTimecards)) {
        subPages.splice(subPages.indexOf("timecards"), 1)
    }
    if (!AccountStore.getCurrentAccountSettings().HierarchySettings.DisableHierarchyItemsForEmployeeUnlessAssigned || !AccountStore.getCurrentAccountSettings().FeatureSettings.EnableHierarchy) {
        subPages.splice(subPages.indexOf("hierarchies"), 1)
    }
    if (!permissionHelper.hasPermission(AuthenticationStore.Permissions.PunchRecords.ViewRecords)) {
        subPages.splice(subPages.indexOf("records"), 1)
    }
    if (!permissionHelper.hasPermission(AuthenticationStore.Permissions.Devices.ViewUserDevices)) {
        subPages.splice(subPages.indexOf("devices"), 1)
    }
    return subPages
}

export function checkValidateSupervisors(isEmployee: boolean, supervisorIds: string[], employeeDefaultHierarchyItemId: string): Promise<string> {
    //not any restriction to assign supervisor as Employee's supervisor
    return new Promise((resolve, reject) => resolve(""))
    return isEmployee ?
        supervisorIds && supervisorIds.length > 0 ?
            api.getManyUsers(supervisorIds).then(res =>
                res.data.find(i => !getSupervisorAccessPermissionForHierarchyId(employeeDefaultHierarchyItemId, i)) ?
                    strings.Invalid_supervisor : ""
            ) :
            new Promise(resolve => resolve("")) :
        new Promise((resolve, reject) => resolve(""))
}

export function checkSupervisorAccessToEmployeeAnyPunchLevel(supervisor: ISupervisor, employeeDefaultHierarchyItemId: string) {
    const employeeAccessHierarchyIds = hierarchyHelper.getHierarchyIdsForShowAllChildHierarchyData(true, employeeDefaultHierarchyItemId)
    const supervisorAccessHierarchyIds = getSupervisorAccessHierarchyIds(supervisor)
    return supervisorAccessHierarchyIds.find(i => employeeAccessHierarchyIds.includes(i)) !== undefined ? true : false
}

export function checkLoggedInUserPermission(employee: IUserDocument | IUIUser | ITimecardEmployee): boolean {
    // let's not check for now.
    // why?
    // supervisor has not permission for employee.
    // supervisor has permission for job.
    // employee has punchset for this job.
    // in this case, supervisor should able to see the employee timecard from job->timecard
    return true
    // if (!employee) return true
    // const loggedInUser = AuthenticationStore.getLoggedInUser()
    // const permission = loggedInUser.AccountUser ?
    //     getSupervisorAccessHierarchyIds().includes(employee.DefaultPunchHierarchyId ? employee.DefaultPunchHierarchyId : null) :
    //     employee.Id === loggedInUser.Id

    // !permission &&
    //     AppStateStore.setNoAccessPage()

    // return permission
}

export function isESSAction(employeeId: string) {
    const loggedInUserId = AuthenticationStore.getLoggedInUser().Id
    if (permissionHelper.getLoggedInUserType() !== ELoggedInUserType.Employee) {
        if (employeeId !== loggedInUserId) {
            return false
        }
        else {
            return !permissionHelper.hasPermission(AuthenticationStore.Permissions.Employees.AllowSelfEmpEdit)
        }
    }
    else {
        return true
    }
}